import { useMemo, useState } from "react";
import { X } from "lucide-react";

import { HubUserAvatar } from "@/components/HubUserAvatar";
import { Label } from "@/components/ui/Input";
import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";

import {
  formatHubUserShortLabel,
  hubUsersToSearchableOptions,
  type HubUserSelectOption,
} from "./hubUserSelectOptions";

export type { HubUserSelectOption } from "./hubUserSelectOptions";
export { formatHubUserShortLabel, hubUsersToSearchableOptions } from "./hubUserSelectOptions";

export type HubUserSearchableMultiSelectProps = {
  id: string;
  label?: string;
  users: HubUserSelectOption[];
  selectedUserIds: string[];
  onSelectedUserIdsChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  noneSelectedLabel?: string;
  /** Inline chips in a bordered field (default), or a single-line summary trigger. */
  variant?: "chips" | "compact" | "default";
  /** Label on the add control when `variant` is `chips`. */
  addLabel?: string;
};

function HubUserRecipientChip({
  user,
  disabled,
  onRemove,
}: {
  user: HubUserSelectOption;
  disabled: boolean;
  onRemove: () => void;
}): JSX.Element {
  const shortLabel = formatHubUserShortLabel(user);
  return (
    <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800">
      <HubUserAvatar
        avatarUrl={null}
        label={shortLabel}
        size="sm"
        className="h-4 w-4 text-[9px]"
      />
      <span className="truncate">{shortLabel}</span>
      <button
        type="button"
        className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
        aria-label={`Remove ${shortLabel}`}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </span>
  );
}

export function HubUserSearchableMultiSelect({
  id,
  label,
  users,
  selectedUserIds,
  onSelectedUserIdsChange,
  disabled = false,
  placeholder = "Select hub users…",
  noneSelectedLabel = "No recipients",
  variant = "chips",
  addLabel = "Add…",
}: HubUserSearchableMultiSelectProps): JSX.Element {
  const useChipField = variant !== "compact" && variant !== "default";
  const [searchValue, setSearchValue] = useState("");
  const options = useMemo(() => hubUsersToSearchableOptions(users), [users]);
  const selectedValues = useMemo(() => new Set(selectedUserIds), [selectedUserIds]);
  const usersById = useMemo(() => new Map(users.map((user) => [user.userId, user])), [users]);
  const inactiveSelectedUserIds = useMemo(
    () => selectedUserIds.filter((userId) => !usersById.has(userId)),
    [selectedUserIds, usersById],
  );

  function renderChip({
    option,
    disabled: chipDisabled,
    onRemove,
  }: {
    option: SearchableSelectOption;
    disabled: boolean;
    onRemove: () => void;
  }): JSX.Element {
    const user = usersById.get(option.value);
    if (user != null) {
      return <HubUserRecipientChip user={user} disabled={chipDisabled} onRemove={onRemove} />;
    }
    return (
      <span className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800">
        <span className="truncate">{option.label}</span>
        <button
          type="button"
          className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
          aria-label={`Remove ${option.label}`}
          disabled={chipDisabled}
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          <X className="h-3 w-3" aria-hidden />
        </button>
      </span>
    );
  }

  const picker = (
    <SearchableMultiSelect
      id={id}
      selectedValues={selectedValues}
      onSelectedValuesChange={(next) => onSelectedUserIdsChange([...next])}
      options={options}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      placeholder={placeholder}
      searchPlaceholder="Search hub users…"
      noneSelectedLabel={noneSelectedLabel}
      emptyMessage="No hub users match."
      disabled={disabled}
      panelMinWidth={320}
      variant={useChipField ? "chips" : "compact"}
      addLabel={addLabel}
      renderChip={useChipField ? renderChip : undefined}
    />
  );

  const inactiveRecipientWarning =
    inactiveSelectedUserIds.length > 0 ? (
      <p className="text-sm text-amber-700" role="status">
        {inactiveSelectedUserIds.length} saved recipient
        {inactiveSelectedUserIds.length === 1 ? " is" : "s are"} no longer an active hub user and will not
        receive notifications. Remove {inactiveSelectedUserIds.length === 1 ? "them" : "those users"} from
        this list.
      </p>
    ) : null;

  if (label != null && label !== "") {
    return (
      <div>
        <Label htmlFor={id}>{label}</Label>
        <div className="mt-1">{picker}</div>
        {inactiveRecipientWarning}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {picker}
      {inactiveRecipientWarning}
    </div>
  );
}
